// src/pages/commercial/CommercialMeetingsPage.js
import React, { useEffect, useMemo, useState } from 'react';
import CommercialLayout from '../../components/commercial/CommercialLayout';
import { useAuth } from '../../contexts/AuthContext';
import pb from '../../lib/pocketbase';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import frLocale from '@fullcalendar/core/locales/fr';
import AppointmentForm from '../../components/admin/appointments/AppointmentForm';
import './CommercialMeetingsPage.css';

// Ensure FullCalendar CSS is loaded
const ensureFullCalendarCSS = () => {
    const urls = [
        'https://cdn.jsdelivr.net/npm/@fullcalendar/daygrid@6.1.15/main.min.css',
        'https://cdn.jsdelivr.net/npm/@fullcalendar/timegrid@6.1.15/main.min.css',
        'https://cdn.jsdelivr.net/npm/@fullcalendar/list@6.1.15/main.min.css',
    ];
    urls.forEach((href) => {
        if (!document.querySelector(`link[href="${href}"]`)) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = href;
            document.head.appendChild(link);
        }
    });
};

const typeOptions = [
    { value: 'call', label: 'Appel', color: '#64748b' },
    { value: 'visio', label: 'Visio', color: '#0ea5e9' },
    { value: 'rdv', label: 'RDV', color: '#8b5cf6' },
];

const statusOptions = [
    { value: 'proposed', label: 'Proposé', bg: '#f1f5f9', color: '#475569' },
    { value: 'accepted', label: 'Accepté', bg: '#dbeafe', color: '#1e40af' },
    { value: 'canceled', label: 'Annulé', bg: '#fef2f2', color: '#991b1b' },
    { value: 'scheduled', label: 'Planifié', bg: '#e0e7ff', color: '#3730a3' },
    { value: 'done', label: 'Terminé', bg: '#dcfce7', color: '#166534' },
];

export default function CommercialMeetingsPage() {
    const { user } = useAuth();
    useEffect(() => { try { ensureFullCalendarCSS(); } catch { } }, []);

    const [items, setItems] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editing, setEditing] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [filters, setFilters] = useState({ search: '', status: '' });

    const fetchData = async () => {
        setLoading(true);
        try {
            // Load Customers 
            let cRes = [];
            try {
                cRes = await pb.collection('customers').getFullList({ sort: 'name', filter: `created_by = "${user.id}" || commercial_id = "${user.id}"` });
                setCustomers(cRes);
            } catch (e) { console.error("Error loading customers", e); }

            // Load Projects (with fallback)
            try {
                const pRes = await pb.collection('projects').getFullList({
                    sort: '-created',
                    filter: `created_by = "${user.id}" || client.commercial_id = "${user.id}"`,
                    requestKey: null
                });
                setProjects(pRes);
            } catch (e) {
                console.warn("Could not load filtered projects. Fallback.", e);
                try {
                    const pResFallback = await pb.collection('projects').getFullList({ sort: '-created', filter: `created_by = "${user.id}"` });
                    setProjects(pResFallback);
                } catch (ex) { console.error("Error loading projects fallback", ex); }
            }

            // Load Meetings (filter by direct commercial assignment OR via customer)
            try {
                const mRes = await pb.collection('meetings').getFullList({
                    sort: '-start',
                    filter: `commercial_id = "${user.id}" || customer_id.commercial_id = "${user.id}"`
                });
                setItems(mRes);
            } catch (e) { console.error("Error loading meetings", e); }

        } catch (e) {
            console.error("Fatal error fetching meetings data", e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const handleSave = async (payload) => {
        setSaving(true);
        try {
            if (editing?.id) {
                // Ensure commercial_id is set even on update
                await pb.collection('meetings').update(editing.id, { ...payload, commercial_id: user.id });
            } else {
                // Assign commercial_id on creation
                await pb.collection('meetings').create({ ...payload, commercial_id: user.id });
            }
            // Refresh
            const mRes = await pb.collection('meetings').getFullList({
                sort: '-start',
                filter: `commercial_id = "${user.id}" || customer_id.commercial_id = "${user.id}"`
            });
            setItems(mRes);

            setOpenDrawer(false);
            setEditing(null);
        } catch (e) {
            alert('Erreur sauvegarde: ' + (e.message || e));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!editing?.id || !window.confirm('Supprimer ce RDV ?')) return;
        try {
            await pb.collection('meetings').delete(editing.id);
            const mRes = await pb.collection('meetings').getFullList({
                sort: '-start',
                filter: `commercial_id = "${user.id}" || customer_id.commercial_id = "${user.id}"`
            });
            setItems(mRes);
            setOpenDrawer(false);
            setEditing(null);
        } catch (e) { alert('Erreur suppression'); }
    };

    const getCustomerName = (id) => customers.find(c => c.id === id)?.name || '';
    const getProjectName = (id) => { const p = projects.find(i => i.id === id); return p?.name || p?.brand || ''; };

    // Filter Items
    const filteredItems = useMemo(() => {
        return items.filter(m => {
            if (filters.status && m.status !== filters.status) return false;

            if (filters.search) {
                const s = filters.search.toLowerCase();
                const cName = getCustomerName(m.customer_id).toLowerCase();
                const pName = getProjectName(m.project_id).toLowerCase();
                if (!((m.title || '').toLowerCase().includes(s) || cName.includes(s) || pName.includes(s))) return false;
            }
            return true;
        });
    }, [items, filters, customers, projects]);

    // Map to Events
    const events = useMemo(() => filteredItems.map(m => {
        const cust = getCustomerName(m.customer_id);
        const typ = typeOptions.find(t => t.value === m.type);
        const st = statusOptions.find(s => s.value === m.status);

        return {
            id: m.id,
            title: m.title || cust || 'RDV',
            start: m.start,
            end: m.end || m.start,
            backgroundColor: 'transparent',
            borderColor: 'transparent',
            extendedProps: { ...m, custName: cust },
            _typeColor: typ?.color || '#64748b',
            _statusBg: st?.bg || '#f1f5f9',
            _statusColor: st?.color || '#000',
        };
    }), [filteredItems, customers]);

    const onDateSelect = (info) => {
        // Prefill start/end but without ID (so it counts as new)
        setEditing({
            start: info.startStr,
            end: info.endStr
        });
        setSelectedDate(info.startStr.slice(0, 10));
        setOpenDrawer(true);
    };

    const onEventClick = (info) => {
        setEditing({ id: info.event.id, ...info.event.extendedProps });
        setSelectedDate(null);
        setOpenDrawer(true);
    };

    const onEventDrop = async ({ event }) => {
        try {
            const startIso = event.start && !isNaN(event.start.getTime()) ? event.start.toISOString() : null;
            const endIso = event.end && !isNaN(event.end.getTime()) ? event.end.toISOString() : startIso;

            if (!startIso) return;

            await pb.collection('meetings').update(event.id, {
                start: startIso,
                end: endIso
            });
            // Update local state
            setItems(prev => prev.map(m => m.id === event.id ? { ...m, start: startIso, end: endIso } : m));
        } catch { fetchData(); } // Revert if failed
    };

    const renderEventContent = (eventInfo) => {
        const p = eventInfo.event.extendedProps;
        const typeColor = eventInfo.event.extendedProps._typeColor || '#888';

        return (
            <div className="fc-event-content-custom">
                <div className="fc-event-line" style={{ borderLeft: `3px solid ${typeColor}` }}>
                    <div className="fc-time">{eventInfo.timeText}</div>
                    <div className="fc-title">{eventInfo.event.title}</div>
                    {p.custName && <div className="fc-subtitle">{p.custName}</div>}
                </div>
            </div>
        );
    };

    return (
        <CommercialLayout title="">
            <div className="commercial-page-wrapper" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <style>{`
                /* Adapting Admin Styles to Commercial Layout - Scoped */
                .commercial-page-wrapper {
                   --mt-primary: var(--commercial-accent, #4f46e5); 
                   --mt-bg: var(--commercial-bg, #f8fafc);
                   --mt-card: var(--commercial-panel, #ffffff);
                   --mt-border: var(--commercial-border, #e2e8f0);
                   --mt-text-main: var(--commercial-text, #0f172a);
                   --mt-text-muted: var(--commercial-muted, #64748b);
                   
                   /* Map Admin variables for AppointmentForm */
                   --admin-panel: var(--commercial-panel, #fff);
                   --admin-panel-2: var(--commercial-panel-2, #f9fafb);
                   --admin-border: var(--commercial-border, #e2e8f0);
                   --admin-text: var(--commercial-text, #0f172a);
                   --admin-muted: var(--commercial-muted, #64748b);
                   --admin-accent: var(--commercial-accent, #4f46e5);
                }

                .commercial-page-wrapper .meetings-page-container {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                    font-family: 'Inter', sans-serif;
                    height: 100%;
                }

                /* Toolbar */
                .commercial-page-wrapper .meetings-toolbar {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 12px;
                }
                .commercial-page-wrapper .toolbar-left, .commercial-page-wrapper .toolbar-right { display: flex; align-items: center; gap: 10px; }

                .commercial-page-wrapper .search-box {
                    display: flex;
                    align-items: center;
                    background: var(--mt-card);
                    border: 1px solid var(--mt-border);
                    border-radius: 8px;
                    padding: 0 10px;
                    height: 40px;
                    width: 240px;
                    color: var(--mt-text-muted);
                    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
                }
                .commercial-page-wrapper .search-box input {
                    border: none;
                    background: transparent;
                    width: 100%;
                    outline: none;
                    margin-left: 8px;
                    color: var(--mt-text-main);
                    font-size: 0.9rem;
                }

                .commercial-page-wrapper .filter-select {
                    height: 40px;
                    padding: 0 12px;
                    border: 1px solid var(--mt-border);
                    border-radius: 8px;
                    background: var(--mt-card);
                    color: var(--mt-text-main);
                    font-size: 0.9rem;
                    outline: none;
                    cursor: pointer;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
                }

                /* Btn New Meeting (Gradient style) */
                .commercial-page-wrapper .btn-new-meeting {
                    display: inline-flex; 
                    align-items: center; 
                    gap: 10px;
                    padding: 10px 14px; 
                    border-radius: 10px;
                    background: linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85));
                    border: 1px solid rgba(79,70,229,0.6);
                    box-shadow: 0 6px 18px rgba(79,70,229,0.35);
                    color: white;
                    height: auto;
                    font-weight: 500;
                    font-size: 0.9rem;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }
                .commercial-page-wrapper .btn-new-meeting:hover { transform: translateY(-1px); filter: brightness(1.04); }
                .commercial-page-wrapper .btn-new-meeting:active { transform: translateY(0); filter: brightness(0.98); }

                /* Card container for Calendar */
                .commercial-page-wrapper .card-clean {
                    background: var(--mt-card);
                    border: 1px solid var(--mt-border);
                    border-radius: 12px;
                    padding: 16px;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px -1px rgba(0, 0, 0, 0.02);
                }

                /* FullCalendar Customizations - "Clean SaaS" Look */
                .commercial-page-wrapper .fc { font-family: inherit; }
                .commercial-page-wrapper .fc-toolbar-title { font-size: 1.25rem !important; font-weight: 600; color: var(--mt-text-main); }
                .commercial-page-wrapper .fc-button {
                    background: var(--mt-card) !important;
                    border: 1px solid var(--mt-border) !important;
                    color: var(--mt-text-muted) !important;
                    font-weight: 500 !important;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.05) !important;
                    padding: 8px 16px !important;
                    border-radius: 8px !important;
                    margin-left: 4px !important;
                    text-transform: capitalize !important;
                }
                .commercial-page-wrapper .fc-button:hover, .commercial-page-wrapper .fc-button-active {
                    background: var(--mt-primary) !important;
                    border-color: var(--mt-primary) !important;
                    color: white !important;
                    box-shadow: 0 4px 6px rgba(79, 70, 229, 0.2) !important;
                }
                .commercial-page-wrapper .fc-col-header-cell-cushion { color: var(--mt-text-muted); font-weight: 600; padding: 10px 0 !important; text-decoration: none !important; }
                .commercial-page-wrapper .fc-scrollgrid { border: none !important; }
                .commercial-page-wrapper .fc-timegrid-slot { border-bottom: 1px dashed #f1f5f9 !important; }
                .commercial-page-wrapper .fc-theme-standard td, .commercial-page-wrapper .fc-theme-standard th { border-color: #f1f5f9; }

                /* Event Styling */
                .commercial-page-wrapper .fc-event {
                    border: none !important;
                    background: transparent !important;
                    box-shadow: none !important;
                    margin: 1px 2px !important;
                }
                .commercial-page-wrapper .fc-event-content-custom {
                    background: #f8fafc; 
                    border: 1px solid #e2e8f0;
                    border-radius: 6px;
                    padding: 4px;
                    height: 100%;
                    overflow: hidden;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.03);
                    cursor: pointer;
                    transition: transform 0.1s, box-shadow 0.1s;
                }
                .commercial-page-wrapper .fc-event-content-custom:hover {
                    box-shadow: 0 4px 6px rgba(0,0,0,0.05);
                    transform: translateY(-1px);
                    background: var(--mt-card);
                    filter: brightness(1.1);
                }
                .commercial-page-wrapper .fc-event-line {
                    padding-left: 6px;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    height: 100%;
                }
                .commercial-page-wrapper .fc-time { font-size: 0.75rem; color: var(--mt-text-muted); font-weight: 500; }
                .commercial-page-wrapper .fc-title { font-size: 0.85rem; color: var(--mt-text-main); font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
                .commercial-page-wrapper .fc-subtitle { font-size: 0.75rem; color: var(--mt-text-muted); margin-top: 1px; }

                /* Drawer (Side Panel) replaced by Centered Modal */
                .commercial-page-wrapper .drawer-overlay {
                    position: fixed; inset: 0;
                    background: rgba(0,0,0,0.5); /* Darker backdrop */
                    z-index: 2000;
                    opacity: 0; pointer-events: none;
                    transition: opacity 0.2s ease;
                    backdrop-filter: blur(2px);
                    display: flex; align-items: center; justify-content: center; /* Center content */
                }
                .commercial-page-wrapper .drawer-overlay.open { opacity: 1; pointer-events: auto; }
                
                .commercial-page-wrapper .drawer-panel {
                    position: relative;
                    width: 100%; max-width: 720px;
                    background: var(--mt-card);
                    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
                    border-radius: 12px;
                    display: flex; flex-direction: column;
                    max-height: 90vh;
                    transform: scale(0.95);
                    transition: transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                }
                .commercial-page-wrapper .drawer-overlay.open .drawer-panel { transform: scale(1); }

                .commercial-page-wrapper .drawer-header {
                    padding: 20px 24px;
                    border-bottom: 1px solid #e2e8f0;
                    display: flex; justify-content: space-between; align-items: center;
                }
                .commercial-page-wrapper .drawer-header h2 { margin: 0; font-size: 1.25rem; color: var(--mt-text-main); }
                .commercial-page-wrapper .btn-close { background: none; border: none; font-size: 1.25rem; cursor: pointer; color: var(--mt-text-muted); }
                .commercial-page-wrapper .drawer-body { flex: 1; overflow-y: auto; padding: 24px; background: var(--mt-bg); border-radius: 0 0 12px 12px;}
                
                .commercial-page-wrapper .form-wrapper .appointment-form-overlay { position: static; background: transparent; padding: 0; display: block; }
                .commercial-page-wrapper .form-wrapper .appointment-form-container { width: 100%; max-width: none; box-shadow: none; border: none; max-height: none; }
                .commercial-page-wrapper .form-wrapper .appointment-form-header { display: none; }
                
                .commercial-page-wrapper .btn-delete {
                    background: transparent; border: 1px solid #fee2e2; color: #b91c1c;
                    width: 100%; padding: 10px; border-radius: 8px; cursor: pointer;
                }
                .commercial-page-wrapper .btn-delete:hover { background: #fef2f2; }

                /* Hover effect on slots */
                .commercial-page-wrapper .fc-timegrid-slot-lane:hover {
                    cursor: cell;
                    background-color: rgba(79, 70, 229, 0.08) !important;
                    transition: background-color 0.1s;
                }
                .commercial-page-wrapper .fc-timegrid-slot-lane:hover::after { content: none; }
                
                .commercial-page-wrapper .fc-daygrid-day-frame:hover {
                     background-color: rgba(79, 70, 229, 0.05) !important;
                     cursor: cell;
                }
                .commercial-page-wrapper .fc-daygrid-day-frame:hover::after {
                    content: "+";
                    position: absolute; left: 50%; top: 50%;
                    transform: translate(-50%, -50%);
                    color: var(--mt-primary); font-weight: 800; pointer-events: none;
                    font-size: 2rem; opacity: 0.5;
                }
                
                @media (max-width: 640px) {
                    .commercial-page-wrapper .meetings-toolbar { flex-direction: column; align-items: stretch; }
                    .commercial-page-wrapper .search-box { width: 100%; }
                    .commercial-page-wrapper .btn-new-meeting { justify-content: center; }
                }
                /* --- DARK MODE PORT FROM ADMIN --- */
                .dark .commercial-page-wrapper .fc-theme-standard th, 
                .dark .commercial-page-wrapper .fc-theme-standard td, 
                .dark .commercial-page-wrapper .fc-theme-standard .fc-scrollgrid {
                    border-color: var(--mt-border) !important;
                }

                .dark .commercial-page-wrapper .fc-theme-standard th {
                    background-color: var(--mt-card) !important;
                }

                .dark .commercial-page-wrapper .fc-col-header-cell-cushion,
                .dark .commercial-page-wrapper .fc-timegrid-slot-label-cushion {
                    color: var(--mt-text-main) !important;
                    opacity: 0.9;
                }

                .dark .commercial-page-wrapper .fc-day-today {
                    background: rgba(99, 102, 241, 0.1) !important;
                }

                .dark .commercial-page-wrapper .fc-list { border-color: var(--mt-border); }
                .dark .commercial-page-wrapper .fc-list-day-cushion { background: var(--mt-bg) !important; }
                .dark .commercial-page-wrapper .fc-list-event:hover td { background: rgba(255,255,255,0.03) !important; }
                .dark .commercial-page-wrapper .fc-list-day-text, .dark .commercial-page-wrapper .fc-list-day-side-text { color: var(--mt-text-main) !important; }
                .dark .commercial-page-wrapper .fc-list-event-title, .dark .commercial-page-wrapper .fc-list-event-time { color: var(--mt-text-main) !important; }
                .dark .commercial-page-wrapper .fc-list-event-dot { border-color: var(--mt-primary); }

                /* Hover effects on dark grid */
                .dark .commercial-page-wrapper .fc-timegrid-slot-lane:hover { 
                    background-color: rgba(99, 102, 241, 0.15) !important; 
                    transition: background-color 0.2s ease;
                }
                .dark .commercial-page-wrapper .fc-daygrid-day-frame:hover { 
                    background-color: rgba(99, 102, 241, 0.15) !important; 
                    transition: background-color 0.2s ease;
                }
                /* Dark Mode Event Cards */
                .dark .commercial-page-wrapper .fc-event-content-custom {
                    background: var(--mt-bg) !important;
                    border-color: var(--mt-border) !important;
                }
                .dark .commercial-page-wrapper .fc-event-content-custom:hover {
                    background: var(--mt-card) !important;
                }
                .dark .commercial-page-wrapper .fc-title { color: var(--mt-text-main) !important; }
            `}</style>

                {/* Header */}
                <div className="meetings-toolbar">
                    <div className="toolbar-left">
                        <h1 style={{ margin: 0, fontSize: '1.8rem', color: 'var(--mt-text-main)' }}>Agenda</h1>
                        <div className="search-box" style={{ marginLeft: 16 }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                            <input
                                type="text"
                                placeholder="Rechercher un RDV..."
                                value={filters.search}
                                onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                            />
                        </div>
                    </div>

                    <div className="toolbar-right">
                        <select
                            className="filter-select"
                            value={filters.status}
                            onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
                        >
                            <option value="">Tous les statuts</option>
                            {statusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>

                        <button
                            className="btn-new-meeting"
                            onClick={() => { setEditing(null); setSelectedDate(new Date().toISOString().split('T')[0]); setOpenDrawer(true); }}
                        >
                            + Nouveau RDV
                        </button>
                    </div>
                </div>

                {/* Calendar */}
                <div className="calendar-wrapper card-clean" style={{ flex: 1 }}>
                    {loading && <div style={{ padding: 20 }}>Chargement...</div>}
                    <FullCalendar
                        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                        initialView="timeGridWeek"
                        headerToolbar={{
                            left: 'prev,next today',
                            center: 'title',
                            right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
                        }}
                        locale={frLocale}
                        editable={true}
                        selectable={true}
                        selectMirror={true}
                        dayMaxEvents={true}
                        weekends={true}
                        events={events}
                        select={onDateSelect}
                        eventClick={onEventClick}
                        eventDrop={onEventDrop}
                        eventResize={onEventDrop}
                        eventContent={renderEventContent}
                        height="auto"
                        slotMinTime="07:00:00"
                        slotMaxTime="20:00:00"
                        allDaySlot={false}
                        slotDuration="00:30:00"
                    />
                </div>

                {/* Drawer */}
                <div className={`drawer-overlay ${openDrawer ? 'open' : ''}`} onClick={() => setOpenDrawer(false)}>
                    <div className="drawer-panel" onClick={e => e.stopPropagation()}>
                        <div className="drawer-header">
                            <h2>{editing?.id ? 'Modifier le Rendez-vous' : 'Nouveau Rendez-vous'}</h2>
                            <button className="btn-close" onClick={() => setOpenDrawer(false)}>✕</button>
                        </div>
                        <div className="drawer-body">
                            {openDrawer && (
                                <div className="form-wrapper">
                                    <AppointmentForm
                                        appointment={editing}
                                        customers={customers}
                                        projects={projects}
                                        onSubmit={handleSave}
                                        onCancel={() => setOpenDrawer(false)}
                                        loading={saving}
                                        selectedDate={selectedDate}
                                    />
                                    {editing?.id && (
                                        <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid #f1f5f9' }}>
                                            <button className="btn-delete" onClick={handleDelete}>
                                                Supprimer ce rendez-vous
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </CommercialLayout>
    );
}
